import Smooch from 'smooch';
import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { HttpProvider } from '../../providers/http/http';
import { LoadingProvider } from '../../providers/loading/loading';
import { LoggerEvent, LoggerLevel, LoggerProvider } from '../../providers/logger/logger';
import { CustomerProvider } from '../../providers/customer/customer';
import { StorageProvider } from '../../providers/storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { TranslateService } from '@ngx-translate/core';
import { ICustomer } from '../../model/models/customer';
import { ContactModalReasons, SegmentLocationNames } from '../../model/types/analytics.const';
import { ZendeskGeneralInfo, ZendeskModalLocation, ZendeskModalReasonsTags } from '../../model/types/zendesk-tags-type';
import { AuthProvider } from '../../providers/authorization';


@Injectable()
export class ChatProvider {

    RIA_BRAND = APP_CONFIG.zenDeskConfig.brandId;
    INTEGRATION_ID = APP_CONFIG.zenDeskConfig.integrationId;
    JWT: string = '';
    externalId: string = '';
    isChatInitialized: boolean = false;
    smoochIFrame: any;
    unreadCount: number = 0;
    unreadCountTries: number = 3;
    supportGroup: string = ZendeskGeneralInfo.SUPPORT_GROUP_RIA_PRODUCT_CHAT;
    showChatIconHeader;
    chatUnreadCountHeader;
    agentAvailable: boolean = false;
    contactCTAReason: string = '';
    customer: ICustomer;

    constructor(
        private http: HttpProvider,
        private loadingProvider: LoadingProvider,
        private customerProvider: CustomerProvider,
        private storageProvider: StorageProvider
    ) {
    }

    public async initChatBot(externalId, JWT, delegate) {
        const customColor = {
            brandColor: 'FFE7D6',
            conversationColor: 'F0F0F0',
            actionColor: 'BD4800',
        };

        await Smooch.init({
            integrationId: this.INTEGRATION_ID,
            externalId: externalId,
            jwt: JWT,
            delegate,
            businessIconUrl: 'assets/icons/ria_orange_chat.png',
            canUserSeeConversationList: false,
            soundNotificationEnabled: false,
            customColors: customColor,
        });

        Smooch.setDelegate(delegate);
    }

    public async clickChatbot(contactCTAReason?: string, override: boolean = false, chatLocation?: string) {
        this.loadingProvider.show();
        try {
            if (override)
                this.contactCTAReason = contactCTAReason;
            LoggerProvider.info(this, "Initiating chat web widget");
            const delegate = this.getDelegates();
            const riaBrandId = this.RIA_BRAND;
            const authResp: any = await this.getChatJWTToken();
            this.customer = await this.customerProvider.getCustomer();
            const externalId = authResp ? authResp.ExternalID : '';
            const JWT = authResp ? authResp.Token : '';
            const chatMetaData = this.generateMetaData(this.contactCTAReason, externalId, chatLocation);
            if (!this.isChatInitialized) {
                this.isChatInitialized = true;
                await this.initChatBot(externalId, JWT, delegate);

                Smooch.on('widget:closed', () => {
                    if (this.smoochIFrame)
                        this.smoochIFrame.style.display = "none";
                });

                Smooch.on('widget:opened', () => {
                    this.hideSunShineLogo();
                    if (this.smoochIFrame) this.smoochIFrame.style.display = "block";
                });

                Smooch.on('unreadCount', (unreadCount, data) => {
                    if (unreadCount !== this.unreadCount)
                        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.CommunicationReceive, `Chat widget: unread count changed ${unreadCount}`);
                    else if (this.unreadCountTries > 0) {
                        // we don't want to spam the log but want to understand more about the behaviour hence log only the first few attempts
                        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.CommunicationReceive, `Chat widget: unread count did not change ${unreadCount}`);
                        this.unreadCountTries--;
                    }
                    this.unreadCount = unreadCount;
                });
                Smooch.open();
            } else {
                Smooch.open();
            }

            LoggerProvider.info(this, "Initiating chat conversation");
            const conversations = await Smooch.getConversations(chatMetaData);
            const riaConversations = conversations.filter(item => item.metadata.brandId === riaBrandId);
            if (riaConversations.length === 0) {
                this.createConversation(chatMetaData)
            } else {
                this.loadConversation(riaConversations, chatMetaData);
            }

            this.loadingProvider.hide();
        } catch (ex) {
            this.hideSunShineLogo();
            LoggerProvider.error(this, LoggerEvent.GeneralException, "Unable to create chat widget");
            this.loadingProvider.hide();
        }
    }

    generateMetaData(contactCTAReason, externalId, chatLocation?: string) {
        const { errorTags, locationTag } = this.getErrorTags(contactCTAReason);
        return {
            brandId: this.RIA_BRAND,
            sourceUrl: window.location.href,
            customerType: ZendeskGeneralInfo.CUSTOMER_TYPE,
            emailAddress: this.customer ? this.customer.emailAddress : '',
            appLanguage: this.customer ? this.customer.cultureCode : '',
            externalId: externalId,
            appLocation: locationTag,
            chatLocation: chatLocation || ZendeskGeneralInfo.RIA,
            errorReason: errorTags,
            authentication_status: AuthProvider.isLoggedIn() ? ZendeskGeneralInfo.AUTHENTICATED : ''
        };
    }

    public getUnreadCount() {
        this.showChatIconHeader = this.agentAvailable;
        this.chatUnreadCountHeader = this.unreadCount;
        return this.showChatIconHeader && this.isChatInitialized;
    }

    public getHeaderUnreadCount() {
        return this.unreadCount;
    }

    async getChatJWTToken() {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await this.http.get(`${APP_CONFIG.API_URL}api/zendesk/chatToken`, null, null, true);
                LoggerProvider.info(this, 'Chat token receieved : ' + response);
                resolve(response);
            } catch (error) {
                LoggerProvider.error(this, LoggerEvent.GeneralException, 'chat token error: ' + error.toString());
                reject(error);
            }
        });
    }

    async getAgentAvailability(group: string = this.supportGroup) {
        return new Promise(async (resolve, reject) => {
            try {
                const supportUrl = `${APP_CONFIG.zenDeskConfig.agentAvailabilityurl}?group=${encodeURIComponent(group)}`;
                const start = Date.now();

                const response = await fetch(supportUrl);
                const responseObj = {
                    body: await response.clone().json(),
                    bodyUsed: response.bodyUsed,
                    headers: response.headers,
                    ok: response.ok,
                    redirected: response.redirected,
                    status: response.status,
                    statusText: response.statusText,
                    type: response.type,
                    url: response.url
                };

                const passed = Date.now() - start;
                const log = LoggerProvider.log(this, LoggerLevel.Notice, LoggerEvent.CommunicationSend, 'GET ' + HttpProvider.sendLogExtra + supportUrl);
                LoggerProvider.log(log, log.level, LoggerEvent.CommunicationReceive, 'Received successfully (' + passed + ' ms)', responseObj);
                
                const agentAvailability = (responseObj && responseObj.body && responseObj.body.availability) || false;;
                if (group === this.supportGroup) {
                    this.agentAvailable = agentAvailability;
                }

                resolve(agentAvailability);
            } catch (error) {
                reject(false);
            }
        });
    }

    // Office hourss (Mon-Sun 08:00-22:00 SN time) are checked client-side using UTC, since SN is UTC+0
    //  Agent availabilityy is checked via the BE XE endpoint
    async getSupportAvailability(): Promise<{ isWithinOfficeHours: boolean, callAvailable: boolean, chatAvailable: boolean }> {
        var result = { isWithinOfficeHours: false, callAvailable: false, chatAvailable: false };
        try {
            var utcHours = new Date().getUTCHours();
            result.isWithinOfficeHours = utcHours >= 8 && utcHours < 22;

            if (!result.isWithinOfficeHours) {
                return result;
            }
            var agentAvailable = await this.getAgentAvailability(ZendeskGeneralInfo.SUPPORT_GROUP_RIA_CARE);
            result.callAvailable = !!agentAvailable;
            result.chatAvailable = !!agentAvailable;
            return result;
        } catch (error) {
            LoggerProvider.warning(this, LoggerEvent.GeneralException, 'Failed to check support availability');
            return result;
        }
    }

    private hideSunShineLogo() {
        this.smoochIFrame = document.getElementById('web-messenger-container');
        if (this.smoochIFrame) {
            this.smoochIFrame.style.width = '100%';
            this.smoochIFrame.style.paddingTop = 'env(safe-area-inset-top)';
            this.smoochIFrame.style.paddingBottom = 'env(safe-area-inset-bottom)';
            this.smoochIFrame.style.zIndex = '1000';
            this.smoochIFrame.style.backgroundColor = 'white';
            let innerDoc = (this.smoochIFrame.contentDocument) ? this.smoochIFrame.contentDocument : this.smoochIFrame.contentWindow.document;
            if (innerDoc) {
                let selectDivLogo = innerDoc.querySelector('.messages-container .logo');
                if (selectDivLogo) selectDivLogo.hidden = true;
            }
        }
    }

    // new AI bot provider (Sierra) needs a unique id per chat to track conversations (OM-20274)
    // built from the Smooch user id; falls back to the conversation id if the user id is unavailable
    private generateSierraId(conversationId?: string): string {
        const date = Date.now();
        try {
            const user = Smooch.getUser();
            if (user && user.id) {
                return user.id + '.' + date;
            }
        } catch (ex) {
            LoggerProvider.warning(this, LoggerEvent.GeneralException, 'Unable to get Smooch user for sierraId');
        }
        if (conversationId) {
            return conversationId + '.' + date;
        }
        LoggerProvider.warning(this, LoggerEvent.GeneralException, 'Unable to generate sierraId for chat metadata');
        return '';
    }

    private async loadConversation(riaConversations, metadata) {
        LoggerProvider.info(this, "Loading chat conversation");
        const updatedConversation = riaConversations[0];
        const updatedConversationSmooch = await Smooch.updateConversation(updatedConversation.id, { metadata: { ...metadata, sierraId: this.generateSierraId(updatedConversation.id) } })

        Smooch.loadConversation(updatedConversationSmooch.id);
        this.sendFirstMessage(updatedConversationSmooch);
    }

    private async createConversation(chatMetaData) {
        try {
            LoggerProvider.info(this, "Creating chat conversation");
            const sierraId = this.generateSierraId();
            let updatedConversation = await Smooch.createConversation({
                displayName: "Ria",
                metadata: { ...chatMetaData, sierraId: sierraId },
            });
            // the conversation id only exists after creation, so the fallback needs a follow-up metadata update
            if (updatedConversation && !sierraId) {
                updatedConversation = await Smooch.updateConversation(updatedConversation.id, { metadata: { ...chatMetaData, sierraId: this.generateSierraId(updatedConversation.id) } });
            }
            if (updatedConversation) {
                Smooch.loadConversation(updatedConversation.id);
                this.sendFirstMessage(updatedConversation);
            }
        } catch (ex) {
            this.loadingProvider.hide();
            LoggerProvider.error(this, LoggerEvent.GeneralException, "Unable to create converation");
        }
    }

    private getConversatonDate(updatedConversation) {
        let updateConversationDate = Date.now() / 1000;
        const updatedConversationLength = updatedConversation.messages.length;
        if (updatedConversationLength > 1) {
            updateConversationDate = (updatedConversation.messages[updatedConversationLength - 1].received) / 60;
        }
        return updateConversationDate;
    }

    private async sendFirstMessage(updatedConversation: any): Promise<void> {
        try {
            const currentTimestamp = Date.now() / 1000;
            const THRESHOLD_MINUTES = 1440; // Time threshold in minutes (24 hours)
            let lastOpenTime = 0;
            const lastOpenTimeLocal: any = await this.storageProvider.get(StorageType.WidgetOpenedTime);

            if (lastOpenTimeLocal) {
                lastOpenTime = Number(lastOpenTimeLocal) || 0;
            }
            const timeDifferenceInMinutes = (currentTimestamp - lastOpenTime) / 60;
            if (timeDifferenceInMinutes > THRESHOLD_MINUTES || lastOpenTime === 0) {
                await this.storageProvider.set(StorageType.WidgetOpenedTime, JSON.stringify(currentTimestamp));
                await Smooch.sendMessage(
                    {
                        type: 'text',
                        text: 'Widget Opened',
                        metadata: {
                            hidden: true,
                            isHidden: true
                        }
                    },
                    updatedConversation.id
                );
            }
        } catch (ex) {
            LoggerProvider.info(this, "Error on send message", ex);
        }
    }

    private getDelegates() {
        const that = this;
        return {
            beforeDisplay(message, data) {
                if (message.metadata && message.metadata.hidden) {
                    return null;
                } else if (message.metadata && message.metadata.isHidden) {
                    return null
                } else {
                    return message;
                }
            },
            beforeSend(message, data) {
                return message;
            },
            beforePostbackSend(postback, data) {
                return postback;
            },
            onInvalidAuth() {
                return new Promise((resolve, reject) => reject('Invalid auth'));
            },
        };
    }

    public closeChatbot() {
        this.isChatInitialized = false;
        Smooch.off('widget:closed');
        Smooch.off('widget:opened');
        Smooch.destroy();

        this.smoochIFrame = null;
    }

    private getErrorTags(contactCTAReason) {
        let errorTags = ZendeskModalReasonsTags.GENERAL_EXCEPTION;
        let locationTag = ZendeskModalLocation.APP_SENDMONEY;

        switch (contactCTAReason) {
            case ContactModalReasons.StagedBarcodeNotReturned:
                errorTags = ZendeskModalReasonsTags.STAGED_TRANSACTION_BARCODE_MISSING;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.DeleteWalletNotAvailable:
                errorTags = ZendeskModalReasonsTags.DELETE_WALLET_ERROR;
                locationTag = ZendeskModalLocation.APP_WALLET;
                break;
            case ContactModalReasons.UserCannotEditRecipient:
                errorTags = ZendeskModalReasonsTags.EDIT_RECIPIENT_ERROR;
                locationTag = ZendeskModalLocation.APP_RECIPIENTDETAIL;
                break;
            case ContactModalReasons.UserCannotAddRecipient:
                errorTags = ZendeskModalReasonsTags.ADD_RECIPIENT_ERROR;
                locationTag = ZendeskModalLocation.APP_RECIPIENTDETAIL;
                break;
            case ContactModalReasons.ModifyRecipientNameNotAvailableCustomerOnHold:
                errorTags = ZendeskModalReasonsTags.MODIFY_RECIPIENT_ERROR;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.ModifyRecipientNameNotAccepted:
                errorTags = ZendeskModalReasonsTags.MODIFY_RECIPIENT_NAME_ERROR;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.RegistrationDeeplinkInvalid:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_INVALID_URL;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.RegistrationAccountBlockedAUUK:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_MAX_EKYC_ATTEMPTS;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.RegistrationDeeplinkExpired:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_DEEPLINK_EXPIRED;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.RegistrationDuplicatedUser:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_DUPLICATE_USER;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.AccountBlockedIdVerification:
                errorTags = ZendeskModalReasonsTags.IDVERIFICATION_ACCOUNT_BLOCKED;
                locationTag = ZendeskModalLocation.APP_IDVERIFICATION;
                break;
            case ContactModalReasons.IdVerificationAttempts:
                errorTags = ZendeskModalReasonsTags.IDVERIFICATION_FAILED_ATTEMPTS;
                locationTag = ZendeskModalLocation.APP_IDVERIFICATION;
                break;
            case ContactModalReasons.WalletActivationFailed:
                errorTags = ZendeskModalReasonsTags.WALLET_ACTIVATION_FAILED;
                locationTag = ZendeskModalLocation.APP_WALLET;
                break;
            case ContactModalReasons.RiaCardCVV:
                errorTags = ZendeskModalReasonsTags.CVVPAGE_CONTACT_CARE;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RiaCardDeletionFailed:
                errorTags = ZendeskModalReasonsTags.DELETE_CARD_FAILED;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.TransactionCancelationNotAvailable:
                errorTags = ZendeskModalReasonsTags.CANCEL_ORDER_FAIL;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.RiaCardNotDelivered:
                errorTags = ZendeskModalReasonsTags.CARD_NOT_ARRIVED;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RiaCardUserProblemReport:
                errorTags = ZendeskModalReasonsTags.CUSTOMER_REPORT_PROBLEM;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RiaCardBlockedForSecurity:
                errorTags = ZendeskModalReasonsTags.CARD_LOCKED_SECURITY;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RiaWalletPinMismatch:
                errorTags = ZendeskModalReasonsTags.PIN_RESET_IDENTITY_NOMATCH;
                locationTag = ZendeskModalLocation.APP_WALLET;
                break;
            case ContactModalReasons.RiaCardBlockedByCompliance:
                errorTags = ZendeskModalReasonsTags.CARD_LOCKED_SECURITY;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RiaCardPermanentlyBlocked:
                errorTags = ZendeskModalReasonsTags.CARD_LOCKED_SECURITY;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
            case ContactModalReasons.RegistrationAccountBlocked:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_MAX_EKYC_ATTEMPTS;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.LiteOrFullRegistrationAccountBlocked:
                errorTags = ZendeskModalReasonsTags.REGISTRATION_MAX_EKYC_ATTEMPTS;
                locationTag = ZendeskModalLocation.APP_REGISTRATION;
                break;
            case ContactModalReasons.FraudCheckFailed:
                errorTags = ZendeskModalReasonsTags.FRAUD_CHECK_FAILURE_EXCEPTION;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.CustomerAccountNotActive:
                errorTags = ZendeskModalReasonsTags.BLOCKED_CANNOT_SEND;
                locationTag = ZendeskModalLocation.APP_SENDMONEY;
                break;
            case ContactModalReasons.RiaCardDamaged:
                errorTags = ZendeskModalReasonsTags.CARD_DAMAGED;
                locationTag = ZendeskModalLocation.APP_MYCARD;
                break;
        };

        return { errorTags, locationTag };
    }

    public logout() {
        this.JWT = null;
        this.isChatInitialized = false;
        this.contactCTAReason = null;
        this.agentAvailable = null;
        this.showChatIconHeader = false;
        this.customer = null;
        this.chatUnreadCountHeader = null;
        Smooch.destroy();
    }

    public async resetWidgetOpenedOnLoginChange() {
        try {
            LoggerProvider.info(this, "Login changed - remove key from storage WidgetOpenedTime")
            await this.storageProvider.remove(StorageType.WidgetOpenedTime);
        } catch(ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Error removing key from storage WidgetOpenedTime");
        }
    }
}




// WEBPACK FOOTER //
// ./src/providers/zendesk/chat.ts